import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from "react";
import { Row, Col, FormGroup, FormControl } from "react-bootstrap";

import Cleave from "cleave.js/react";

import FormErrorBlock from "../../../components/FormErrorBlock";
import TermsCheckbox from "../../../components/Common/TermsCheckbox";
import { CardLogo } from "./CardLogo";
import { ls } from "../../../locale";
import { validator } from "./validator";
import { getGatewayFromBins } from "./paymentUtils";

const createInitialValues = () => ({
  type: "",
  prevType: "",
  cardNumber: null,
  nameOnCard: null,
  expiration: null,
  cvc: null,
  eppSelected: false,
});

const createInitialErrors = () => ({
  cardNumber: null,
  nameOnCard: null,
  expiration: null,
  cvc: null,
});

const isErrorFree = errors => !Object.values(errors).some(Boolean);

export const CsTabPane = forwardRef(({
  localDebitBins,
  onCreditCardNumberChange,
  eppEligible,
  onEppSelectionChange
}, ref) => {
  const rPayment = ls.t("Payment");
  if (!rPayment?.CreditCard) return null;

  const rootClassName = ls.t("rootClass");
  const autoCompleteSeed = useMemo(() => new Date().getMilliseconds(), []);
  const [values, setValues] = useState(createInitialValues);
  const [errors, setErrors] = useState(createInitialErrors);

  // unselect EPP when it becomes ineligible
  useEffect(() => {
    if (eppEligible) return;

    setValues(prev => {
      if (!prev.eppSelected) return prev;
      onEppSelectionChange?.(false);
      return { ...prev, eppSelected: false };
    });
  }, [eppEligible]);

  const clearErrors = () => setErrors(createInitialErrors());

  const resetValues = () => {
    setValues(createInitialValues());
    clearErrors();
  };

  const updateField = (field, rawValue) => {
    setValues(prev => {
      let value = rawValue;
      if (typeof value === "string") value = value.trimStart();
      if (field === "type" && value === "unknown") value = null;

      const next = { ...prev, [field]: value };

      if (field === "cardNumber") {
        const type = getGatewayFromBins(value, localDebitBins);
        if (type === "mada") {
          next.type = type;
        } else if (prev.type === "mada") {
          next.type = prev.prevType || "";
        }
      } else if (field === "type") {
        next.prevType = value || "";
      }

      return next;
    });

    setErrors(prev =>
      Object.prototype.hasOwnProperty.call(prev, field) ? { ...prev, [field]: null } : prev
    );
  };

  const handleExpirationChange = value => {
    if (value) value = value.trim();
    if (value && value.endsWith("//")) return;

    const regex = /^\d{2}$/;
    if (regex.test(value)) {
      if (values.expiration === value + "/") {
        value = value.slice(0, 1);
      } else {
        value += "/";
      }
    }

    updateField("expiration", value);
  };

  const handleCvcChange = value => {
    if (value) value = value.trim();
    const regex = /^\d{0,3}$/;
    if (!regex.test(value)) return;

    updateField("cvc", value);
  };

  const handleCardBlur = () => {
    if (!values.cardNumber || typeof onCreditCardNumberChange !== "function") return;

    const sanitized = values.cardNumber.replace(/\s/g, "");
    const type = validator.getCreditCardType(sanitized);
    if (type) onCreditCardNumberChange(sanitized);
  };

  const handleEppToggle = () => {
    if (!eppEligible || typeof onEppSelectionChange !== "function") return;

    setValues(prev => {
      const nextSelected = !prev.eppSelected;
      onEppSelectionChange(nextSelected);
      return { ...prev, eppSelected: nextSelected };
    });
  };

  const validateFormFields = () => {
    let cardNumber = values.cardNumber;
    let cardType = null;
    if (typeof cardNumber === "string") {
      cardNumber = cardNumber.replace(/ /g, "");
      cardType = validator.getCreditCardType(cardNumber);
    }

    const nextErrors = {
      cardNumber: !cardType ? validator.invalidField("CardNumber") : null,
      nameOnCard: validator.validateCreditCardName(values.nameOnCard),
      expiration: validator.validateCreditCardExpiration(values.expiration),
      cvc: validator.validateCreditCardCvc(values.cvc)
    };

    setErrors(nextErrors);

    if (cardType) {
      setValues(prev => ({ ...prev, type: cardType, prevType: cardType }));
    }

    return isErrorFree(nextErrors);
  };

  useImperativeHandle(
    ref,
    () => ({
      reset: resetValues,
      clearErrors,
      validate: validateFormFields,
      isValid: () => isErrorFree(errors),
      getValues: () => ({
        type: values.type,
        prevType: values.prevType,
        cardNumber: values.cardNumber,
        nameOnCard: values.nameOnCard,
        expiration: values.expiration,
        cvc: values.cvc,
        eppSelected: values.eppSelected,
        error: errors
      }),
      setCardNumber: value => updateField("cardNumber", value || "")
    }),
    [errors, values]
  );

  return (  
    <Row className="credit-cards">
      <Col md={12}>
        <FormGroup
          className={
            "credit-card-number " +
            rootClassName +
            (errors.cardNumber ? " has-error" : "")
          }
        >
          <Cleave
            value={values.cardNumber || ""}
            placeholder={rPayment.CreditCard.Number}
            className="form-control"
            options={{
              creditCard: true,
              onCreditCardTypeChanged: type => updateField("type", type)
            }}
            onChange={event => updateField("cardNumber", event.target.value)}
            onBlur={handleCardBlur}
          />
          <CardLogo cardType={values.type} />
          <FormErrorBlock error={errors.cardNumber} />
        </FormGroup>
      </Col>
      <Col md={12}>
        <FormGroup className={errors.nameOnCard ? "has-error" : null}>
          <FormControl
            type="text"
            placeholder={rPayment.CreditCard.NameOnCard}
            value={values.nameOnCard || ""}
            autoComplete={autoCompleteSeed}
            onChange={event => updateField("nameOnCard", event.target.value)}
          />
          <FormErrorBlock error={errors.nameOnCard} />
        </FormGroup>
        <span className="expiration-label">{rPayment.CreditCard.Expiration}</span>
      </Col>

      <Col md={6}>
        <FormGroup className={errors.expiration ? "has-error" : null}>
          <Cleave
            value={values.expiration || ""}
            placeholder="MM/YY"
            className="form-control"
            autoComplete={autoCompleteSeed}
            options={{ date: true, delimiter: "/", datePattern: ["m", "y"] }}
            onChange={event => handleExpirationChange(event.target.value)}
          />
          <FormErrorBlock error={errors.expiration} />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup className={errors.cvc ? "has-error" : null}>
          <FormControl
            type="text"
            placeholder={rPayment.CreditCard.SecurityCode}
            value={values.cvc || ""}
            autoComplete={autoCompleteSeed}
            onChange={event => handleCvcChange(event.target.value)}
          />
          <FormErrorBlock error={errors.cvc} />
        </FormGroup>
      </Col>
      {(eppEligible || values.eppSelected) && (
        <Col md={12}>
          <TermsCheckbox
            checked={values.eppSelected === true}
            onClick={handleEppToggle}
            terms={rPayment.EPP}
          />
        </Col>
      )}
    </Row>
  );
});
